import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { POLICIES } from '../../constants/policies';
import { acceptPolicy } from '../../api/user/consent.api';

/**
 * Modal điều khoản tái dùng: hiện nội dung điều khoản của 1 tính năng + tick đồng ý + nút "Đồng ý & tiếp tục".
 * Khi đồng ý → ghi nhận ở BE (theo phiên bản hiện tại) → gọi onAccepted().
 */
const PolicyConsentModal: React.FC<{
  open: boolean;
  policyKey: string;
  onClose: () => void;
  /** Chạy sau khi đã ghi nhận đồng ý; trả Promise thì modal chờ, lỗi thì báo và giữ modal mở. */
  onAccepted: () => void | Promise<void>;
  /** Phần thêm ngay dưới nội dung điều khoản (vd ô mã số thuế). */
  children?: React.ReactNode;
  submitLabel?: string;
  submitDisabled?: boolean;
}> = ({ open, policyKey, onClose, onAccepted, children, submitLabel = 'Đồng ý & tiếp tục', submitDisabled = false }) => {
  const doc = POLICIES[policyKey];
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  // Mỗi lần mở là một lần đồng ý rõ ràng: đóng thì bỏ tick.
  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  if (!open || !doc) return null;

  const submit = async () => {
    if (!checked || submitDisabled) return;
    try {
      setBusy(true);
      await acceptPolicy(policyKey);
      await onAccepted();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ghi nhận đồng ý thất bại.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 shadow-xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b ui-divider">
          <h3 className="font-semibold ui-text">{doc.title}</h3>
          <p className="text-[11px] ui-muted">Phiên bản {doc.version}</p>
        </div>
        <div className="px-5 py-3 overflow-y-auto text-sm ui-text space-y-2">
          {doc.body.map((p, i) => (
            <p key={i} className="leading-relaxed">{p}</p>
          ))}
          {children}
        </div>
        <div className="px-5 py-3 border-t ui-divider space-y-3">
          <label className="flex items-start gap-2 text-sm ui-text cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>Tôi đã đọc và đồng ý với {doc.title}.</span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <button onClick={onClose} className="text-sm ui-muted px-3 py-2">Huỷ</button>
            <button
              onClick={submit}
              disabled={!checked || busy || submitDisabled}
              className="rounded-md bg-[#0071A6] text-white text-sm px-4 py-2 hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Đang lưu…' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyConsentModal;
